import { useEffect, useRef } from 'react';

export interface IUseListagemKeyboardNavParams {
  rowsLength: number;
  pagina: number;
  totalPaginas: number;
  onIrParaPagina: (novaPagina: number, foco: 'primeiro' | 'ultimo') => void;
  onEnter?: (index: number) => void;
  // Lista de tipos de busca pro F8 ciclar entre eles — omitir desativa o atalho.
  tiposBusca?: string[];
  tipoAtual?: string;
  onTrocarTipo?: (novoTipo: string) => void;
  // Dependências do efeito de foco pendente ao chegar numa nova página — normalmente `[rows]`.
  rowsDep: unknown[];
}

export interface IUseListagemKeyboardNavResult {
  pesquisaRef: React.RefObject<HTMLInputElement>;
  linhaRefCallback: (index: number) => (el: HTMLTableRowElement | null) => void;
  onBuscaKeyDown: (event: React.KeyboardEvent) => void;
  onLinhaKeyDown: (index: number) => (event: React.KeyboardEvent) => void;
}

// Navegação por teclado das listagens: foco automático na busca ao entrar na tela, F8 cicla o
// tipo de busca (sem tirar o foco da pesquisa), Espaço fora de um campo de texto devolve o foco
// pra pesquisa, seta-baixo a partir da busca vai pra primeira linha, setas navegam entre linhas
// e pulam de página no limite (focando o primeiro/último item assim que a nova página chega —
// a lista é assíncrona, não dá pra focar antes dos dados existirem), Enter abre o item focado.
export function useListagemKeyboardNav(params: IUseListagemKeyboardNavParams): IUseListagemKeyboardNavResult {
  const { rowsLength, pagina, totalPaginas, onIrParaPagina, onEnter, tiposBusca, tipoAtual, onTrocarTipo, rowsDep } = params;

  const pesquisaRef = useRef<HTMLInputElement>(null);
  const linhaRefs = useRef<Array<HTMLTableRowElement | null>>([]);
  const focoPendenteRef = useRef<'primeiro' | 'ultimo' | null>(null);

  useEffect(() => {
    pesquisaRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F8' && tiposBusca && tiposBusca.length > 0 && onTrocarTipo) {
        event.preventDefault();
        const indiceAtual = tiposBusca.indexOf(tipoAtual ?? '');
        const proximoIndice = indiceAtual === tiposBusca.length - 1 ? 0 : indiceAtual + 1;
        onTrocarTipo(tiposBusca[proximoIndice]);
        pesquisaRef.current?.focus();
        return;
      }

      if (event.key === ' ') {
        const alvo = event.target as HTMLElement;
        const digitando = alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable;
        if (digitando) return;
        event.preventDefault();
        pesquisaRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tiposBusca, tipoAtual, onTrocarTipo]);

  useEffect(() => {
    if (!focoPendenteRef.current || rowsLength === 0) return;
    const alvo = focoPendenteRef.current === 'primeiro' ? 0 : rowsLength - 1;
    focoPendenteRef.current = null;
    linhaRefs.current[alvo]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, rowsDep);

  const linhaRefCallback = (index: number) => (el: HTMLTableRowElement | null) => {
    linhaRefs.current[index] = el;
  };

  const onBuscaKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      linhaRefs.current[0]?.focus();
    }
  };

  const onLinhaKeyDown = (index: number) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onEnter?.(index);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (index < rowsLength - 1) {
        linhaRefs.current[index + 1]?.focus();
      } else if (pagina < totalPaginas) {
        focoPendenteRef.current = 'primeiro';
        onIrParaPagina(pagina + 1, 'primeiro');
      }
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index > 0) {
        linhaRefs.current[index - 1]?.focus();
      } else if (pagina > 1) {
        focoPendenteRef.current = 'ultimo';
        onIrParaPagina(pagina - 1, 'ultimo');
      }
    }
  };

  return { pesquisaRef, linhaRefCallback, onBuscaKeyDown, onLinhaKeyDown };
}
